import React, { useMemo, useState } from "react";

import {
  formatFractionDisplay,
  queryIsLoading,
  useGetBeefCowTypes,
  useGetBeefFeedAdditives,
  useGetBeefFeedTypes,
  useGetBeefManureStorageTypes,
  useGetFootprintingBeefFeedAdditive,
  useGetFootprintingBeefFeedInformation,
  useGetFootprintingBeefFeedProductions,
  useGetFootprintingBeefHerdDetails,
  useGetFootprintingBeefManureAdditive,
  useGetFootprintingBeefManureStorage,
  useGetManureAdditives,
  useTranslation,
} from "utils";

import { getUnitDisplayLabel } from "consts";

import { BeefFootprintReturnBody, FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE } from "models";

import { Card, ListCard } from "components";

import { FootprintingBeefFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingBeefFeedProductionDrawer";
import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { BeefFeedAdditiveForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedAdditiveForm";
import { BeefFeedForageLossForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedForageLossForm";
import { BeefFeedInformationForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedInformationForm";
import { BeefFeedPastureForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedPastureForm";
import { BeefHerdDetailsForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefHerdDetailsForm";
import { BeefHerdOffspringForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefHerdOffspringForm";
import { BeefHerdSizeForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefHerdSizeForm";
import { BeefManureAdditiveForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefManureAdditiveForm";
import { BeefManureStorageForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefManureStorageForm";
import { BeefMoorlandForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefMoorlandForm";
import { BeefYieldForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefYieldForm";
import { BeefCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface FootprintEditBeefDetailsCardProps {
  beefFootprintData: BeefFootprintReturnBody;
  isBeefFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setBeefModalForm: (formType: string) => void;
  farmId: string;
  year: number;
}

const FootprintEditBeefDetailsCard = ({
  beefFootprintData,
  isBeefFootprintDataLoading,
  setShowEditModal,
  setBeefModalForm,
  farmId,
  year,
}: FootprintEditBeefDetailsCardProps) => {
  const { t } = useTranslation(["footprintingBeef", "footprinting"]);

  const cowTypesQuery = useGetBeefCowTypes({ enabled: true });
  const feedTypesQuery = useGetBeefFeedTypes();
  const feedAdditivesQuery = useGetBeefFeedAdditives();
  const manureStorageQuery = useGetBeefManureStorageTypes();
  const manureAdditivesQuery = useGetManureAdditives();

  const { footprint } = beefFootprintData;

  const herdDetailsQuery = useGetFootprintingBeefHerdDetails(footprint?.id ?? "", { enabled: !!footprint?.id });
  const feedInformationQuery = useGetFootprintingBeefFeedInformation(footprint?.id ?? "", { enabled: !!footprint?.id });
  const feedAdditiveUsageQuery = useGetFootprintingBeefFeedAdditive(footprint?.id ?? "", { enabled: !!footprint?.id });
  const manureStorageUsageQuery = useGetFootprintingBeefManureStorage(footprint?.id ?? "", { enabled: !!footprint?.id });
  const manureAdditiveUsageQuery = useGetFootprintingBeefManureAdditive(footprint?.id ?? "");

  const selfProducedFeeds = useMemo(() => (feedInformationQuery.data ?? []).filter((feedInfo) => feedInfo.isSelfProduced), [feedInformationQuery.data]);
  const feedProductionQueries = useGetFootprintingBeefFeedProductions(selfProducedFeeds.map((feedInfo) => feedInfo.id));

  const [selectedFeedInformationId, setSelectedFeedInformationId] = useState<string | null>(null);

  // Verified footprints are read-only: the admin unverify is the only write the API accepts
  const editButton = (formKey: string) =>
    footprint?.isVerified
      ? undefined
      : {
          label: t("footprinting:review.edit"),
          onClick: () => {
            setShowEditModal(true);
            setBeefModalForm(formKey);
          },
        };

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`footprinting:radioAnswer.${isActive}`, { ns: "footprinting" }));
  const isLoading =
    isBeefFootprintDataLoading ||
    [
      cowTypesQuery,
      feedTypesQuery,
      feedAdditivesQuery,
      manureStorageQuery,
      manureAdditivesQuery,
      herdDetailsQuery,
      feedInformationQuery,
      feedAdditiveUsageQuery,
      manureStorageUsageQuery,
      manureAdditiveUsageQuery,
    ].some(queryIsLoading) ||
    feedProductionQueries.some(queryIsLoading);

  return (
    <Card className="admin-edit-beef-details-card padding-125">
      <ReviewPageTitle title={t("review.beef.title", { ns: "footprinting" })} iconName="klim/cow" isLoading={isLoading} />
      {/* Beef yield */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.beefYield.cardTitle")}
        titleButton={editButton(BeefYieldForm.Key)}
        dataPairs={[
          {
            label: t("beefYieldForm.question.beefYieldAmountLabel"),
            value: `${footprint?.beefYieldKgPerAnimal ?? ""}`,
          },
          {
            label: t("beefYieldForm.radioQuestion"),
            value: footprint?.meatProduction ? t(`beefYieldForm.radioOptions.${footprint.meatProduction}`) : "",
          },
        ]}
      />
      {/* Herd size */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.herdSize.cardTitle")}
        titleButton={editButton(BeefHerdSizeForm.Key)}
        dataPairs={[
          ...(footprint?.herdSize
            .filter(({ cowCount }) => (cowCount ?? 0) > 0)
            .map((herdSizeInfo) => ({
              label: cowTypesQuery.data?.find((ct) => ct.id === herdSizeInfo.cowTypeId)?.name ?? "",
              value: `${herdSizeInfo.cowCount}`,
            })) ?? []),
          {
            label: t("reviewAndSubmit.herdSize.calves"),
            value: `${herdDetailsQuery.data?.calvesAmount ?? ""}`,
          },
        ]}
      />
      {/* Herd details */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.herdDetails.cardTitle")}
        titleButton={editButton(BeefHerdDetailsForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.herdDetails.cowLosses"),
            value: `${herdDetailsQuery.data?.avgCowLosses ?? ""}`,
          },
          {
            label: t("reviewAndSubmit.herdDetails.usefulLifespan"),
            value: `${herdDetailsQuery.data?.avgCowUsefulLifeMonths ?? ""} ${t("beefHerdDetailsForm.question.avgCowUsefulLifeMonthsSuffix")}`,
          },
        ]}
      />
      {/* Herd offspring */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.herdOffspring.cardTitle")}
        titleButton={editButton(BeefHerdOffspringForm.Key)}
        dataPairs={[
          {
            label: t("beefHerdOffspringForm.question.fatteningStartWeightInput"),
            value: `${herdDetailsQuery.data?.fatteningStartWeightKg ?? ""} kg`,
          },
          {
            label: t("beefHerdOffspringForm.question.calfMilkFeedingPeriodDaysInput"),
            value: `${herdDetailsQuery.data?.calfMilkFeedingPeriodDays ?? ""} days`,
          },
          {
            label: t("beefHerdOffspringForm.question.rearingTimeDaysInput"),
            value: `${herdDetailsQuery.data?.rearingTimeDays ?? ""} days`,
          },
        ]}
      />
      {/* Feed pasture */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedPasture.cardTitle")}
        titleButton={editButton(BeefFeedPastureForm.Key)}
        dataPairs={[
          {
            label: t("beefFeedPastureForm.cowPastureRadioQuestion"),
            value: footprint?.cowsInPastureActive ? t(`beefFeedPastureForm.cowPastureOptions.${footprint.cowsInPastureActive}`) : "",
          },
          ...(footprint?.cowsInPastureActive === "yes"
            ? [
                {
                  label: t("beefFeedPastureForm.question.cowsInPastureInput"),
                  value: ` ${footprint?.cowsInPasture ?? 0} ${t("beefFeedPastureForm.question.cowsInPastureSuffix")}`,
                },
                {
                  label: t("beefFeedPastureForm.question.daysInPastureInput"),
                  value: ` ${footprint?.daysInPasturePerYear ?? 0} ${t("beefFeedPastureForm.question.daysInPastureSuffix")}`,
                },
                {
                  label: t("beefFeedPastureForm.question.avgHoursInPastureInput"),
                  value: ` ${footprint?.avgHoursInPasturePerDay ?? 0} ${t("beefFeedPastureForm.question.avgHoursInPastureSuffix")}`,
                },
              ]
            : []),
        ]}
      />
      {/* Feed types / amounts */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedInformation.cardTitle")}
        titleButton={editButton(BeefFeedInformationForm.Key)}
        dataPairs={(feedInformationQuery.data ?? []).map((feedInfo) => {
          const feedType = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedTypeId);
          return {
            label: feedType?.name ?? "",
            value: `${feedInfo.energyValue} ${getUnitDisplayLabel(feedInfo.energyUnit)}`,
          };
        })}
      />
      {/* Self-produced feed production */}
      {selfProducedFeeds.map((feedInfo, index) => {
        const feedTypeName = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedTypeId)?.name ?? "";
        const production = feedProductionQueries[index]?.data;
        const isSplitRelevant = production?.cropUsedForFeed === FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO;

        return (
          <ListCard
            key={feedInfo.id}
            className="margin-bottom-100"
            isOutlined
            isLoading={isLoading}
            title={t("reviewAndSubmit.feedInformation.selfProducedTitle", { feedTypeName: feedTypeName || t("beefFeedProductionForm.title") })}
            titleButton={footprint?.isVerified ? undefined : { label: t("footprinting:review.edit"), onClick: () => setSelectedFeedInformationId(feedInfo.id) }}
            dataPairs={[
              {
                label: t("beefFeedProductionForm.question.cropUsageRadioTitle"),
                value: production?.cropUsedForFeed ? t(`beefFeedProductionForm.cropUsageOptions.${production.cropUsedForFeed}`) : "",
              },
              { label: t("beefFeedProductionForm.question.cropYieldTPerHa"), value: `${production?.cropYieldTPerHa ?? ""}` },
              { label: t("beefFeedProductionForm.question.dieselConsumptionLHa"), value: `${production?.dieselConsumptionLHa ?? ""}` },
              { label: t("beefFeedProductionForm.question.energyUsedKwhHa"), value: `${production?.energyUsedKwhHa ?? ""}` },
              { label: t("beefFeedProductionForm.question.mineralNitrogenKgHa"), value: `${production?.mineralNitrogenKgHa ?? ""}` },
              { label: t("beefFeedProductionForm.question.mineralPhosphorusKgHa"), value: `${production?.mineralPhosphorusKgHa ?? ""}` },
              { label: t("beefFeedProductionForm.question.mineralPotassiumKgHa"), value: `${production?.mineralPotassiumKgHa ?? ""}` },
              { label: t("beefFeedProductionForm.question.nitrogenOrganicFertKgHa"), value: `${production?.nitrogenOrganicFertKgHa ?? ""}` },
              { label: t("beefFeedProductionForm.question.totalLimeAppliedKgCaCo3PerHa"), value: `${production?.totalLimeAppliedKgCaCo3PerHa ?? ""}` },
              {
                label: t("beefFeedProductionForm.question.totalPlantProtectionApplied"),
                value: `${production?.totalPlantProtectionApplied ?? ""} ${production?.totalPlantProtectionAppliedUnit ?? ""}`.trim(),
              },
              {
                label: t("beefFeedProductionForm.question.totalWaterUsedIrregationLPerHa"),
                value: `${production?.totalWaterUsedIrregationLPerHa ?? ""}`,
              },
              ...(isSplitRelevant
                ? [
                    { label: t("beefFeedProductionForm.question.soldOffFarmPct"), value: `${formatFractionDisplay(production?.soldOffFarmPct ?? 0)} %` },
                    { label: t("beefFeedProductionForm.question.fedToCowsPct"), value: `${formatFractionDisplay(production?.fedToCowsPct ?? 0)} %` },
                    { label: t("beefFeedProductionForm.question.otherUsesPct"), value: `${formatFractionDisplay(production?.otherUsesPct ?? 0)} %` },
                  ]
                : []),
            ]}
          />
        );
      })}
      {/* Feed additives */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedAdditives.cardTitle")}
        titleButton={editButton(BeefFeedAdditiveForm.Key)}
        dataPairs={[
          {
            label: t("beefFeedAdditiveForm.feedAdditivesQuestion"),
            value: footprint?.feedAdditiveActive ? t(`beefFeedAdditiveForm.feedAdditiveOptions.${footprint.feedAdditiveActive}`) : "",
          },
          ...(feedAdditiveUsageQuery.data ?? []).map((feedAdditiveUsage) => ({
            label: feedAdditivesQuery.data?.find((fa) => fa.id === feedAdditiveUsage.feedAdditiveId)?.name ?? "",
            value: `${feedAdditiveUsage.amount} ${feedAdditiveUsage.amountUnit}`,
          })),
        ]}
      />
      {/* Forage loss */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedForageLoss.cardTitle")}
        titleButton={editButton(BeefFeedForageLossForm.Key)}
        dataPairs={[
          {
            label: t("beefFeedForageLossForm.forageLossQuestion"),
            value: `${formatFractionDisplay(footprint?.forageLossPct ?? null)} %`,
          },
        ]}
      />
      {/* Manure storage */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureStorage.cardTitle")}
        titleButton={editButton(BeefManureStorageForm.Key)}
        dataPairs={[
          ...(manureStorageUsageQuery.data ?? []).map((manureStorageUsage) => ({
            label: manureStorageQuery.data?.find(({ id }) => id === manureStorageUsage.manureStorageId)?.name ?? "Unknown",
            value: `${formatFractionDisplay(manureStorageUsage.amountStoredPct)} %`,
          })),
          {
            label: t("beefManureStorageForm.question.gasTightStorageTypeQuestionInput"),
            value: `${formatFractionDisplay(footprint?.amountStoredGasTankPct ?? 0)} %`,
          },
        ]}
      />
      {/* Manure additives */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureAdditive.cardTitle")}
        titleButton={editButton(BeefManureAdditiveForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.manureAdditive.sluryAcidifiersUsed"),
            value: displayRadioAnswer((manureAdditiveUsageQuery.data?.length ?? 0) > 0),
          },
          ...(manureAdditiveUsageQuery.data ?? []).map((manureAdditiveUsage) => ({
            label: manureAdditivesQuery.data?.find(({ id }) => manureAdditiveUsage.manureAdditiveId === id)?.name ?? "",
            value: `${manureAdditiveUsage.amountUsedM3}`,
          })),
        ]}
      />
      {/* Moorland */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.moorland.cardTitle")}
        titleButton={editButton(BeefMoorlandForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.moorland.moorlandUsed"),
            value: displayRadioAnswer(footprint?.moorlandActive === "yes"),
          },
          ...(footprint?.moorlandActive === "yes"
            ? [
                {
                  label: t("reviewAndSubmit.moorland.fodderArea"),
                  value: `${footprint.fodderProductionAreaHa} ${t("beefMoorlandForm.question.fodderProductionAreaSuffix")}`,
                },
                {
                  label: t("reviewAndSubmit.moorland.averageWaterLevel"),
                  value: `${footprint.annualWaterLevelCm} ${t("beefMoorlandForm.question.annualWaterLevelSuffix")}`,
                },
              ]
            : []),
        ]}
      />
      {/* Comment */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("commentForm.title", { ns: "footprinting" })}
        titleButton={editButton(BeefCommentForm.Key)}
        dataPairs={[
          {
            label: t("commentForm.label", { ns: "footprinting" }),
            value: footprint?.comment || t("commentForm.noComment", { ns: "footprinting" }),
          },
        ]}
      />
      {selectedFeedInformationId && (
        <FootprintingBeefFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedInformationId !== null}
          onClose={() => setSelectedFeedInformationId(null)}
          feedInformationId={selectedFeedInformationId}
          primaryDataId={footprint?.id}
          feedTypeName={
            feedTypesQuery.data?.find(({ id }) => id === selfProducedFeeds.find((feedInfo) => feedInfo.id === selectedFeedInformationId)?.feedTypeId)?.name
          }
        />
      )}
    </Card>
  );
};

export default FootprintEditBeefDetailsCard;
